/**
 * Where "New workflow" now lands. The blank editor asked an ontology question
 * first — known event or new one — which is the wrong first thing to see. A
 * gallery answers "what should happen?" with things that already happen here,
 * and "build it myself" is still one click away for people who want it.
 *
 * Layout follows the approved mockup: search + family filter over a card grid,
 * each card a tinted glyph, a title, what it does, and the step kinds it uses.
 * The whole card is the target — there is no separate button on it.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Input } from '@servicecompressionllc/sc-design-system';
import { ArrowLeft, Plus, Search, Sparkles } from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { TEMPLATE_FAMILIES, WORKFLOW_TEMPLATES, type TemplateFamily } from './templates.js';

/** Semantic tokens only — the glyph takes its meaning from the family, and
 * both flip with the theme. No brand or per-industry colour lives here. */
const TONE: Record<TemplateFamily, string> = {
  Feedback: 'border-info-border bg-info-soft text-info',
  'Your own events': 'border-outline-strong bg-surface text-ink-muted',
};

export const TemplateGalleryPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [family, setFamily] = useState<TemplateFamily | 'All'>('All');

  const needle = query.trim().toLowerCase();
  const shown = WORKFLOW_TEMPLATES.filter(
    (t) =>
      (family === 'All' || t.family === family) &&
      (needle === '' ||
        `${t.title} ${t.blurb} ${t.kinds.join(' ')}`.toLowerCase().includes(needle)),
  );

  return (
    <AppShell>
      <Link
        to="/settings/workflows"
        className="mb-2.5 inline-flex items-center gap-2 text-[0.8rem] text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Workflows
      </Link>
      <h1 className="text-xl font-semibold">What should happen?</h1>
      <p className="mb-5 text-sm text-ink-muted">
        Pick something close to what you want — every part of it can be changed afterwards.
      </p>

      {/* First, because it's the only way in that doesn't ask you to already
          know what you want. It hands over to the editor with the chat open —
          a workflow belongs in the editor, so you watch it appear there rather
          than in a panel floating over a grid of other options. */}
      <button
        type="button"
        onClick={() => navigate('/settings/workflows/new?chat=1')}
        className="mb-5 flex w-full flex-wrap items-center gap-4 rounded-lg border border-outline bg-surface-elevated p-4 text-left transition-colors hover:bg-surface-hover"
      >
        <span
          aria-hidden
          className="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-info-border bg-info-soft text-info"
        >
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[0.95rem] font-semibold text-ink">
            Describe it in your own words
          </span>
          <span className="block text-[0.8rem] leading-5 text-ink-muted">
            “When a unit alarms twice in a week, tell the regional manager” — the helper asks you
            the rest and fills in the workflow.
          </span>
        </span>
        <span className="flex-none rounded border border-outline bg-surface px-3 py-1.5 text-sm font-medium text-ink">
          Describe it
        </span>
      </button>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex min-w-[16rem] flex-1 items-center gap-2 rounded border border-outline-strong bg-surface px-3">
          <Search className="h-4 w-4 flex-none text-ink-subtle" />
          <Input
            aria-label="Search starting points"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search — try "bad answer", "alarm", "approve"'
            className="border-0 bg-transparent px-0 focus-visible:ring-0"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(['All', ...TEMPLATE_FAMILIES] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={family === f}
              onClick={() => setFamily(f)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                family === f
                  ? 'border-primary bg-primary text-white'
                  : 'border-outline-strong text-ink-muted hover:text-ink'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {shown.map((t) => (
          <Card
            key={t.id}
            padding="md"
            interactive
            role="button"
            tabIndex={0}
            aria-label={t.title}
            onClick={() => navigate(`/settings/workflows/new?from=${t.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ')
                navigate(`/settings/workflows/new?from=${t.id}`);
            }}
            className="flex min-w-0 cursor-pointer flex-col gap-2.5"
          >
            <span
              aria-hidden
              className={`flex h-8 w-8 items-center justify-center rounded-md border ${TONE[t.family]}`}
            >
              <t.icon className="h-4 w-4" />
            </span>
            <h2 className="text-[0.95rem] font-semibold text-ink">{t.title}</h2>
            <p className="flex-1 text-[0.8rem] leading-5 text-ink-muted">{t.blurb}</p>
            <div className="flex flex-wrap gap-1.5">
              {t.kinds.map((k) => (
                <span
                  key={k}
                  className="rounded border border-outline bg-surface px-1.5 py-0.5 text-[0.68rem] font-medium text-ink-muted"
                >
                  {k}
                </span>
              ))}
            </div>
          </Card>
        ))}
        {shown.length === 0 && (
          <p className="text-sm text-ink-muted">
            Nothing matches that — clear the search, or start from scratch below.
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 rounded-lg border border-dashed border-outline-strong p-4">
        <span
          aria-hidden
          className="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-outline text-ink-muted"
        >
          <Plus className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[0.95rem] font-semibold text-ink">Start from scratch</h2>
          <p className="text-[0.8rem] leading-5 text-ink-muted">
            Choose what starts it, then add the steps yourself.
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/settings/workflows/new')}>
          Build it myself
        </Button>
      </div>
    </AppShell>
  );
};
